import { useEffect, useState } from 'react';

import { AppIcon, CountPill, GenericTable, Pagination } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  coachingAnalyticsApi,
  type StudentListRequest,
  type StudentRow,
  type StudentWhereClause,
  useListCoachingAnalyticsStudentsQuery,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { downloadBlob } from '@backrs_ui/grow-ui/src/utils/downloadFile';

import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Snackbar,
  type SxProps,
  TableCell,
  TableRow,
  type Theme,
  Typography,
} from '@mui/material';

import { mutedTextSx } from './analytics.styles';
import { useAnalyticsFilter } from './AnalyticsFilterContext';
import { useAgentContext } from './StudentDataModal.agentContext';
import {
  cellsFor,
  displayColumns,
  fieldsFor,
  PHOTO_FIELD,
  sortFieldOf,
  type StudentColumn,
  toCsv,
  toTsv,
} from './viewData.columns';

const { viewData, states } = appStrings.modules.coachingAnalytics;

const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 200;
const TOAST_MS = 3000;

/** A clicked chart element: the clauses that narrow to it and the chip that names it. */
export type SegmentFilter = Readonly<{
  label: string;
  where: readonly StudentWhereClause[];
  /** Replaces the card's columns while this segment is shown. */
  columns?: readonly StudentColumn[];
}>;

type Sort = Readonly<{ by: string; direction: 'asc' | 'desc' }>;

const DEFAULT_SORT: Sort = { by: 'last_name', direction: 'asc' };

export type StudentDataModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  id: string;
  title: string;
  columns: readonly StudentColumn[];
  note?: string;
  segment?: SegmentFilter;
  onClearSegment?: () => void;
}>;

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const today = () => new Date().toISOString().slice(0, 10);

export const StudentDataModal = ({
  open,
  onClose,
  id,
  title,
  columns: cardColumns,
  note,
  segment,
  onClearSegment,
}: StudentDataModalProps) => {
  const { filter, summary } = useAnalyticsFilter();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const columns = segment?.columns ?? cardColumns;
  const [toast, setToast] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const where = segment?.where ?? [];
  const shownColumns = displayColumns(columns);
  const hasPhoto = shownColumns.some((col) => col.id === 'name');
  const fields = hasPhoto ? [...fieldsFor(columns), PHOTO_FIELD] : fieldsFor(columns);
  const request: StudentListRequest = {
    filter,
    where,
    fields,
    sort,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };

  // Fresh cohort, fresh first page.
  useEffect(() => setPage(1), [segment, filter, sort]);
  // The segment may swap the columns, and the sort field must be one of them.
  useEffect(() => {
    if (open) setSort(DEFAULT_SORT);
  }, [open, segment]);

  const toggleSort = (by: string) =>
    setSort((current) => ({
      by,
      direction: current.by === by && current.direction === 'asc' ? 'desc' : 'asc',
    }));

  const { data, isFetching, isError } = useListCoachingAnalyticsStudentsQuery(request, {
    skip: !open,
  });

  const rows = data?.students ?? [];
  const total = data?.matched_total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  useAgentContext({ open, title, segment: segment?.label, total, columns, rows });

  const copyPage = async () => {
    try {
      await navigator.clipboard.writeText(toTsv(columns, rows));
      setToast(viewData.copied(rows.length));
    } catch (error) {
      console.error('Copying the student list failed', error);
      setToast(viewData.copyFailed);
    }
  };

  const downloadAll = async () => {
    setIsExporting(true);
    try {
      const all: StudentRow[] = [];

      for (let offset = 0; ; offset += EXPORT_PAGE_SIZE) {
        const result = await dispatch(
          coachingAnalyticsApi.endpoints.listCoachingAnalyticsStudents.initiate(
            { ...request, fields: fieldsFor(columns), limit: EXPORT_PAGE_SIZE, offset },
            { subscribe: false },
          ),
        ).unwrap();

        all.push(...result.students);
        if (result.returned < EXPORT_PAGE_SIZE || all.length >= result.matched_total) break;
      }

      downloadBlob(
        new Blob([toCsv(columns, all)], { type: 'text/csv;charset=utf-8' }),
        `analytics-${slug(title)}-${today()}.csv`,
      );
    } catch (error) {
      console.error(`Exporting the "${title}" student list failed`, error);
      setToast(viewData.downloadFailed);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog id={id} open={open} onClose={onClose} maxWidth={false} sx={dialogSx}>
      <Box sx={headerSx}>
        <Box sx={titleRowSx}>
          <Box sx={headerLeadSx}>
            <Box sx={headerTextSx}>
              <Typography variant="text-lg-semibold">{title}</Typography>
              {summary.map((part) => (
                <Chip key={part} size="small" label={part} sx={filterChipSx} />
              ))}
              {segment ? (
                <Chip
                  id={`${id}-segment-chip`}
                  size="small"
                  label={segment.label}
                  onDelete={onClearSegment}
                  sx={filterChipSx}
                  deleteIcon={
                    <Box component="span" sx={chipDeleteSx}>
                      <AppIcon iconName="XClose" diameter="12px" color="currentColor" />
                    </Box>
                  }
                />
              ) : null}
            </Box>
            {note ? (
              <Typography id={`${id}-note`} variant="text-sm-regular" sx={mutedTextSx}>
                {note}
              </Typography>
            ) : null}
          </Box>
          <IconButton id={`${id}-close-btn`} onClick={onClose} sx={closeSx}>
            <AppIcon iconName="XClose" diameter="20px" color="Colors/Foreground/fg-quaternary" />
          </IconButton>
        </Box>
        <Box sx={actionsSx}>
          <Button
            id={`${id}-copy-btn`}
            hierarchy="Secondary gray"
            size="sm"
            disabled={rows.length === 0}
            onClick={copyPage}
            endIcon={<AppIcon iconName="Copy01" diameter="16px" color="currentColor" />}
          >
            {viewData.copy}
          </Button>
          <Button
            id={`${id}-download-btn`}
            hierarchy="Secondary gray"
            size="sm"
            disabled={total === 0 || isExporting}
            onClick={downloadAll}
            endIcon={<AppIcon iconName="Download01" diameter="16px" color="currentColor" />}
          >
            {isExporting ? viewData.downloading : viewData.download}
          </Button>
        </Box>
      </Box>
      <Box sx={bodySx}>
        {isError ? (
          <Typography variant="text-sm-regular" sx={mutedTextSx}>
            {viewData.error}
          </Typography>
        ) : (
          <Box sx={tableWrapSx(isFetching)}>
            <GenericTable
              headers={shownColumns.map((col) => (
                <SortableHeader
                  key={col.id}
                  id={`${id}-sort-${col.id}`}
                  label={col.header}
                  field={sortFieldOf(col)}
                  sort={sort}
                  onSort={toggleSort}
                />
              ))}
              ariaLabel={title}
              stickyHeader
            >
              {rows.map((row, index) => (
                <TableRow key={`${row.student_id ?? index}`} id={`${id}-row-${index}`}>
                  {cellsFor(shownColumns, row).map((cell, cellIndex) => (
                    <TableCell key={shownColumns[cellIndex].id} sx={cellSx}>
                      {shownColumns[cellIndex].id === 'name' ? (
                        <Box sx={nameCellSx}>
                          <Avatar sx={avatarSx} src={photoOf(row)} alt="" />
                          {cell}
                        </Box>
                      ) : shownColumns[cellIndex].pill && cell ? (
                        <CountPill value={cell} />
                      ) : (
                        cell
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </GenericTable>
            {!isFetching && rows.length === 0 ? (
              <Typography variant="text-sm-regular" sx={emptySx}>
                {viewData.noStudents}
              </Typography>
            ) : null}
          </Box>
        )}
      </Box>
      <Box sx={footerSx}>
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {isFetching && !data ? states.loading : viewData.showing(from, to, total)}
        </Typography>
        {totalPages > 1 ? (
          <Box sx={paginationSx}>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              handlePrevPage={() => setPage((current) => Math.max(1, current - 1))}
              handleNextPage={() => setPage((current) => Math.min(totalPages, current + 1))}
              withPageNumbers
              sticky
              backgroundColor="Colors/Background/bg-primary"
            />
          </Box>
        ) : null}
      </Box>
      <Snackbar
        id={`${id}-toast`}
        open={toast != null}
        onClose={() => setToast(null)}
        autoHideDuration={TOAST_MS}
        message={toast ?? ''}
      />
    </Dialog>
  );
};

const SortableHeader = ({
  id,
  label,
  field,
  sort,
  onSort,
}: Readonly<{
  id: string;
  label: string;
  field: string | undefined;
  sort: Sort;
  onSort: (field: string) => void;
}>) => {
  if (!field) return <>{label}</>;

  const isActive = sort.by === field;

  return (
    <Box
      component="button"
      type="button"
      id={id}
      sx={sortButtonSx(isActive)}
      onClick={() => onSort(field)}
    >
      {label}
      <AppIcon
        iconName={
          isActive
            ? sort.direction === 'desc'
              ? 'ArrowDown'
              : 'ArrowUp'
            : 'ChevronSelectorVertical'
        }
        diameter="14px"
        color="currentColor"
      />
    </Box>
  );
};

const photoOf = (row: StudentRow): string | undefined => {
  const value = row[PHOTO_FIELD];

  return typeof value === 'string' ? value : undefined;
};

const DIALOG_WIDTH = 1200;
const TABLE_MIN_WIDTH = 560;

const dialogSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  '& .MuiDialog-paper': {
    display: 'flex',
    flexDirection: 'column',
    width: DIALOG_WIDTH,
    maxWidth: `calc(100vw - ${backrs.spacing['spacing-3xl']})`,
    height: '85vh',
    overflow: 'hidden',
    borderRadius: backrs.radius['radius-xl'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    [breakpoints.down('sm')]: {
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
      borderRadius: 0,
    },
  },
});

const headerSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  gap: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-3xl'],
  paddingBottom: backrs.spacing['spacing-xl'],
  [breakpoints.down('sm')]: { padding: backrs.spacing['spacing-xl'] },
});

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
});

const closeSx: SxProps<Theme> = {
  flexShrink: 0,
  padding: 0,
};

const headerLeadSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  minWidth: 0,
});

const headerTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  minWidth: 0,
});

const sortButtonSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-xs'],
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    color: isActive ? backrs.colors['Colors/Text/text-brand-secondary'] : 'inherit',
  });

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-md'],
});

const chipDeleteSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
};

const filterChipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-brand-primary'],
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  '& .MuiChip-deleteIcon': { color: 'inherit', marginRight: backrs.spacing['spacing-xs'] },
});

const bodySx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

const tableWrapSx =
  (isFetching: boolean): SxProps<Theme> =>
  ({ backrs, breakpoints, typography }) => ({
    opacity: isFetching ? 0.6 : 1,
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xl'],
    '& .MuiTableContainer-root': {
      flex: '0 1 auto',
      minHeight: 0,
      minWidth: TABLE_MIN_WIDTH,
      overflow: 'auto',
      borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
      backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    },
    '& th': {
      ...typography['text-xs-semibold'],
      whiteSpace: 'nowrap',
      textAlign: 'left',
      color: backrs.colors['Colors/Text/text-quaternary'],
      backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
      paddingBlock: backrs.spacing['spacing-lg'],
      borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    },
    '& th:first-of-type, & td:first-of-type': { paddingLeft: backrs.spacing['spacing-3xl'] },
    '& th:last-of-type, & td:last-of-type': { paddingRight: backrs.spacing['spacing-3xl'] },
    '& td': {
      paddingBlock: backrs.spacing['spacing-xl'],
      borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    },
    [breakpoints.down('sm')]: {
      '& .MuiTableContainer-root': { minWidth: 0 },
      '& th:first-of-type, & td:first-of-type': {
        position: 'sticky',
        left: 0,
        paddingLeft: backrs.spacing['spacing-xl'],
      },
      '& td:first-of-type': {
        zIndex: 1,
        backgroundColor: backrs.colors['Colors/Background/bg-primary'],
      },
      '& th:first-of-type': { zIndex: 3 },
    },
  });

const nameCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const avatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: '40px',
  height: '40px',
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Text/text-tertiary'],
  flexShrink: 0,
});

const cellSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-regular'],
  color: backrs.colors['Colors/Text/text-primary'],
  whiteSpace: 'nowrap',
});

const emptySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textAlign: 'center',
  padding: backrs.spacing['spacing-3xl'],
});

const paginationSx: SxProps<Theme> = {
  '& > .MuiBox-root': { padding: 0, marginInline: 0, borderTop: 'none' },
};

const footerSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  gap: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-xl'],
  paddingInline: backrs.spacing['spacing-3xl'],
  [breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'center',
    gap: backrs.spacing['spacing-md'],
    paddingInline: backrs.spacing['spacing-xl'],
  },
});
